import React from 'react';
import { Column } from 'primereact/column';
import { getFilterTemplates, getColumnTemplates } from '../tableHelpers';
import { FULL_DATE_FORMAT } from '../../../settings';
import {
  ColumnDefinitions,
  ColumnConfig,
  ColumnFactoryProps,
  StringColumnConfig,
  BoolColumnConfig,
  NumberColumnConfig,
  DateColumnConfig,
  EnumColumnConfig,
  MultiEnumColumnConfig,
  MultiReferenceColumnConfig,
} from './types';

/**
 * Creates a Column component based on field name and column configuration
 */
export const createColumnFromDefinition = (
  fieldName: string,
  columnConfig: ColumnConfig,
  props: ColumnFactoryProps
): React.ReactElement => {
  const { filterState, handleChangeFilter, handleFilterApplyClick, t } = props;
  const filterTemplates = getFilterTemplates(t);
  const columnTemplates = getColumnTemplates(t);

  // Common props for all columns (excluding key)
  const commonProps = {
    sortable: columnConfig.sortable !== false,
    frozen: columnConfig.frozen,
    alignFrozen: columnConfig.alignFrozen,
  };

  // Filter props for data columns
  const getFilterProps = (_field: string) => {
    if (columnConfig.filterable === false) {
      return {};
    }

    return {
      filter: true,
      showFilterMenuOptions: false,
      onFilterApplyClick: handleFilterApplyClick,
    };
  };

  switch (columnConfig.type) {
    case 'string': {
      const config = columnConfig as StringColumnConfig;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.stringFilterTemplate(filterState[fieldName]?.value, handleChangeFilter, fieldName)
          }
          onFilterClear={() => handleChangeFilter(fieldName, '', true)}
          body={columnTemplates.bodyTemplate}
        />
      );
    }

    case 'bool': {
      const config = columnConfig as BoolColumnConfig;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.boolFilterTemplate(filterState[fieldName]?.value, handleChangeFilter, fieldName)
          }
          onFilterClear={() => handleChangeFilter(fieldName, null, true)}
          body={(data, props) =>
            columnTemplates.boolColumnTemplate(
              data,
              props,
              config.trueLabel || t('common.yes'),
              config.falseLabel || t('common.no')
            )
          }
        />
      );
    }

    case 'number':
    case 'int': {
      const config = columnConfig as NumberColumnConfig;

      const isIntType = columnConfig.type === 'int';
      const maxFractionDigits = isIntType ? 0 : config.maxFractionDigits || 2;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.numberRangeFilterTemplate(
              filterState[fieldName]?.value,
              handleChangeFilter,
              fieldName,
              maxFractionDigits,
              config.locale
            )
          }
          onFilterClear={() => handleChangeFilter(fieldName, [], true)}
          body={columnTemplates.numberTemplate}
        />
      );
    }

    case 'date': {
      const config = columnConfig as DateColumnConfig;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.dateRangeFilterTemplate(filterState[fieldName]?.value, handleChangeFilter, fieldName)
          }
          onFilterClear={() => handleChangeFilter(fieldName, [], true)}
          body={(data, props) =>
            columnTemplates.calendarColumnTemplate(
              data,
              props,
              config.dateFormat || FULL_DATE_FORMAT,
              config.emptyValue || '-'
            )
          }
        />
      );
    }

    case 'enum': {
      const config = columnConfig as EnumColumnConfig;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.multiselectFilterTemplate(
              filterState[fieldName]?.value,
              handleChangeFilter,
              fieldName,
              config.enumOptions
            )
          }
          onFilterClear={() => handleChangeFilter(fieldName, null, true)}
          body={(data, props) => columnTemplates.enumColumnTemplate(data, props, config.enumTranslationPrefix)}
        />
      );
    }

    case 'multiEnum': {
      const config = columnConfig as MultiEnumColumnConfig;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.multiselectFilterTemplate(
              filterState[fieldName]?.value,
              handleChangeFilter,
              fieldName,
              config.enumOptions
            )
          }
          onFilterClear={() => handleChangeFilter(fieldName, null, true)}
          body={(data, props) => columnTemplates.multiEnumColumnTemplate(data, props, config.enumTranslationPrefix)}
        />
      );
    }

    case 'multiReference': {
      const config = columnConfig as MultiReferenceColumnConfig;

      return (
        <Column
          key={fieldName}
          {...commonProps}
          field={fieldName}
          header={t(config.translationKey)}
          {...getFilterProps(fieldName)}
          filterElement={() =>
            filterTemplates.stringFilterTemplate(filterState[fieldName]?.value, handleChangeFilter, fieldName)
          }
          onFilterClear={() => handleChangeFilter(fieldName, '', true)}
          body={columnTemplates.multiReferenceColumnTemplate}
        />
      );
    }

    default:
      throw new Error(`Unknown column type: ${(columnConfig as any).type}`);
  }
};

/**
 * Hook that provides a function to create columns from definitions
 */
export const useColumnFactory = (
  filterState: any,
  handleChangeFilter: (name: string, value: any, triggerDataRefresh?: boolean) => void,
  handleFilterApplyClick: () => void,
  t: (key: string, options?: any) => string
) => {
  const factoryProps: ColumnFactoryProps = {
    filterState,
    handleChangeFilter,
    handleFilterApplyClick,
    t,
  };

  return {
    createColumn: (fieldName: string, columnConfig: ColumnConfig) =>
      createColumnFromDefinition(fieldName, columnConfig, factoryProps),
    createColumns: (columnDefs: ColumnDefinitions) =>
      Object.entries(columnDefs).map(([fieldName, config]) =>
        createColumnFromDefinition(fieldName, config, factoryProps)
      ),
  };
};
